import * as React from 'react'
import { useEffect } from 'react'

import {
  Box,
  Button,
  type Icon,
  Text,
  theme,
  useClassNames,
} from '@factorialco/deprecated-design-system'
import { AnimatePresence, motion } from 'framer-motion'

import type Popover from 'modules/core/components/PopoverMenu'
import Portal from 'modules/core/components/Portal'

import Footer from './Footer'
import Actions from './_Action'
import * as styles from './index.vanilla.css'

export type Sizes = 'narrow' | 'wide' | 'wideXL' | 'extended' | 'full'

const styleFromSize = (size: Sizes) => {
  switch (size) {
    case 'narrow':
      return { width: '100%', maxWidth: theme.dimensions.s360 }

    case 'wide':
      return { width: '100%', maxWidth: theme.dimensions.s600 }

    case 'wideXL':
      return { width: '100%', maxWidth: theme.dimensions.sidepanelWideXL }

    case 'extended':
      return { width: '100%', maxWidth: '95%' }

    case 'full':
      return { width: '100%', maxWidth: '100%' }

    default:
      return {}
  }
}

export interface Option {
  title: string
  description?: string
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  onClick?: () => void
  to?: string
  href?: React.ComponentProps<typeof Popover.HrefOption>['href']
  download?: React.ComponentProps<typeof Popover.HrefOption>['download']
  target?: React.ComponentProps<typeof Popover.HrefOption>['target']
  danger?: boolean
}

interface Props {
  title?: string | React.ReactNode
  width?: Sizes
  overlay?: boolean
  locked?: boolean
  open?: boolean
  footer?: React.ReactNode
  onClose?: () => void
  onCloseClicked?: () => void
  headerBorder?: boolean
  boxPadding?: boolean
  closeLabel?: string
  allowBackgroundInteraction?: boolean
  options?: Option[]
}

const EXIT_TRANSITION_DURATION_MS = 200
const EXIT_TRANSITION_DURATION = EXIT_TRANSITION_DURATION_MS / 1000

const SidepanelContent = ({
  title,
  children,
  width = 'narrow',
  overlay = true,
  locked = false,
  footer,
  onClose,
  onCloseClicked,
  headerBorder = true,
  boxPadding = true,
  closeLabel,
  allowBackgroundInteraction = false,
  options,
}: React.PropsWithChildren<Props>) => {
  const cx = useClassNames(styles)
  const className = cx('base')

  return (
    <Portal locked={locked} onClose={onClose}>
      {!allowBackgroundInteraction && (
        <motion.div
          key="opacity-animation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { ease: 'easeOut' } }}
          exit={{ opacity: 0, transition: { duration: EXIT_TRANSITION_DURATION, ease: 'easeIn' } }}
          className={`${styles.overlay} ${overlay ? styles.overlayBackground : ''}`}
          onClick={() => !locked && onClose?.()}
        ></motion.div>
      )}
      <motion.div
        key="sliding-animation"
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1, transition: { ease: 'easeOut' } }}
        exit={{
          x: '100%',
          opacity: 0,
          transition: { duration: EXIT_TRANSITION_DURATION, ease: 'easeIn' },
        }}
        className={`${className} ${styles.sidepanel}`}
        style={styleFromSize(width)}
        aria-label={React.isValidElement(title) ? undefined : (title as string)}
        tabIndex={-1}
        aria-labelledby="sidepanelLabel"
        aria-modal="true"
      >
        <Box boxShadow="s400" background="overlayBackground" minHeight="full" height="full">
          <Box
            flexDirection="row"
            width="full"
            padding="s16"
            paddingX="s24"
            className={styles.Header}
          >
            <Box width="s48" justifyContent="center">
              {onClose && (
                <Button
                  iconName="cross"
                  type="secondary"
                  onClick={(event) => {
                    event.stopPropagation()
                    onClose?.()
                  }}
                  aria-label={closeLabel}
                />
              )}
            </Box>
            <Box width="full" justifyContent="center" alignItems="center" textAlign="center">
              {React.isValidElement(title) ? (
                title
              ) : (
                <Box alignItems="center" justifyContent="center" flexDirection="row" flexGrow>
                  <Text size="300" color="textPrimary" weight="semibold">
                    {title}
                  </Text>
                </Box>
              )}
            </Box>
            <Box width="s48">
              <Actions options={options} />
            </Box>
          </Box>
          <Box
            overflowY="auto"
            padding={boxPadding ? 's24' : null}
            background="overlayBackground"
            flexGrow
          >
            {children}
          </Box>
          <Footer content={footer} />
        </Box>
      </motion.div>
    </Portal>
  )
}

const Sidepanel = (props: React.PropsWithChildren<Props>) => {
  const [isOpen, setIsOpen] = React.useState(props.open)

  useEffect(() => {
    setIsOpen(props.open)
  }, [props.open])

  const handleClose = () => {
    setIsOpen(false)
    props.onCloseClicked?.()
    setTimeout(() => {
      props.onClose?.()
    }, EXIT_TRANSITION_DURATION_MS)
  }

  return (
    <AnimatePresence>
      {isOpen ? <SidepanelContent {...props} onClose={handleClose} /> : null}
    </AnimatePresence>
  )
}

export default Sidepanel
